import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, GraduationCap, CheckCircle2, Briefcase } from "lucide-react";
import { fetchProgram } from "../lib/api";

const ProgramDetail = () => {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProgram(slug)
      .then(setProgram)
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
  }, [slug]);

  if (error === "not_found") {
    return (
      <div className="hccbt-container section text-center" data-testid="program-not-found">
        <h1 className="font-display text-4xl text-[#0F172A]">Program not found</h1>
        <p className="mt-4 text-[#475569]">The program you're looking for isn't available.</p>
        <Link to="/programs" className="btn-primary mt-8 inline-flex">View all programs</Link>
      </div>
    );
  }

  if (!program) {
    return <div className="hccbt-container section text-[#64748B]">Loading program…</div>;
  }

  return (
    <div data-testid="program-detail-page">
      <section className="hccbt-container pt-12 md:pt-16 pb-8">
        <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A]" data-testid="back-to-programs">
          <ArrowLeft size={16} /> Back to Programs
        </Link>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className={`kicker ${program.status === "coming_soon" ? "!bg-amber-100 !text-amber-800" : ""}`}>
            {program.status === "coming_soon" ? "Coming 2026" : "Now enrolling"}
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0F172A] leading-tight">
          {program.title}
        </h1>
        <p className="mt-6 text-lg text-[#475569] leading-relaxed max-w-3xl">{program.summary}</p>

        <div className="mt-8 flex flex-wrap gap-4">
          <div className="card-soft px-5 py-3 inline-flex items-center gap-2">
            <Clock size={16} className="text-[#6B9AC4]" />
            <span className="text-sm"><span className="font-semibold">Duration:</span> {program.duration}</span>
          </div>
          <div className="card-soft px-5 py-3 inline-flex items-center gap-2">
            <GraduationCap size={16} className="text-[#6B9AC4]" />
            <span className="text-sm"><span className="font-semibold">Credential:</span> {program.credential}</span>
          </div>
        </div>
      </section>

      <section className="hccbt-container section pt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <span className="overline">Program highlights</span>
          <h2 className="font-display text-3xl mt-3 text-[#0F172A] leading-tight">What makes this program different</h2>
          <ul className="mt-6 space-y-3">
            {program.highlights?.map((h) => (
              <li key={h} className="flex items-start gap-3 text-[#334155]" data-testid="program-highlight">
                <CheckCircle2 size={18} className="text-[#6B9AC4] mt-1" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <aside className="lg:col-span-5">
          <div className="card-soft p-7">
            <span className="overline">Career outcomes</span>
            <h3 className="font-display text-2xl mt-3 text-[#0F172A]">Where graduates go</h3>
            <ul className="mt-5 space-y-3">
              {program.careers?.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-[#334155]">
                  <Briefcase size={16} className="text-[#6B9AC4] mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <Link
              to={program.status === "active" ? "/admissions" : "/contact"}
              className="btn-primary w-full justify-center mt-7"
              data-testid="program-detail-cta"
            >
              {program.status === "active" ? "Apply now" : "Join the waitlist"}
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default ProgramDetail;
